/**
 * Admin: Notifications inbox + recipient management.
 * - Top half: pending in-app notifications (clickable, mark read, delete).
 * - Bottom half: configure who receives admin email alerts when items await action.
 * Real email delivery happens when ADMIN_NOTIFICATION_PROVIDER=resend and RESEND_API_KEY
 * are set in the backend .env (see playbook). Until then, notifications still queue here.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, Bell, Check, Link2, Mail, Plus, RefreshCw, Send, Trash2 } from 'lucide-react';
import api from '../lib/api';
import HelpCallout from './HelpCallout';

export default function AdminNotifications() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [recipients, setRecipients] = useState([]);
  const [emailConfigured, setEmailConfigured] = useState(false);
  const [provider, setProvider] = useState('none');
  const [newEmail, setNewEmail] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [addErr, setAddErr] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [checkingLinks, setCheckingLinks] = useState(false);
  const [linkResult, setLinkResult] = useState(null);

  async function sendTest() {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post('/admin/notifications/send-test');
      setTestResult(r.data);
      loadNotifications();
    } finally { setTesting(false); }
  }

  async function runLinkCheck() {
    setCheckingLinks(true);
    setLinkResult(null);
    try {
      // Fire-and-forget: backend starts the sweep in the background and returns immediately.
      await api.post('/admin/links/check');
      setLinkResult({ _status: 'running', message: 'Checking 165+ URLs in the background — results appear here in ~20 seconds.' });
      // Poll the latest-run endpoint until the new run finishes (finished_at appears)
      const startedAt = Date.now();
      const initial = await api.get('/admin/links/runs/latest');
      const initialRunId = initial.data?.run?.id || null;
      const poll = async () => {
        try {
          const r = await api.get('/admin/links/runs/latest');
          const run = r.data?.run;
          // New run (different id from before this trigger) AND finished_at present → done.
          if (run && run.id !== initialRunId && run.finished_at) {
            setLinkResult(run);
            loadNotifications();
            setCheckingLinks(false);
            return;
          }
          if (Date.now() - startedAt > 120000) {
            setLinkResult({ _status: 'timeout', message: 'Still running after 2 minutes. Check the notifications inbox shortly.' });
            setCheckingLinks(false);
            return;
          }
          setTimeout(poll, 2500);
        } catch {
          setTimeout(poll, 3000);
        }
      };
      setTimeout(poll, 3000);
    } catch (e) {
      setLinkResult({ _status: 'error', message: e.response?.data?.detail || 'Could not start link check.' });
      setCheckingLinks(false);
    }
  }

  function loadNotifications() {
    api.get('/admin/notifications').then((r) => { setItems(r.data.items); setUnread(r.data.unread); });
  }
  function loadRecipients() {
    api.get('/admin/notification-recipients').then((r) => {
      setRecipients(r.data.items);
      setEmailConfigured(r.data.email_configured);
      setProvider(r.data.provider);
    });
  }
  useEffect(() => { loadNotifications(); loadRecipients(); }, []);

  async function markRead(id) { await api.post(`/admin/notifications/${id}/read`); loadNotifications(); }
  async function readAll() { await api.post('/admin/notifications/read-all'); loadNotifications(); }
  async function delN(id) { await api.delete(`/admin/notifications/${id}`); loadNotifications(); }

  async function addRecipient(e) {
    e.preventDefault();
    setAddErr('');
    try {
      await api.post('/admin/notification-recipients', { email: newEmail, label: newLabel });
      setNewEmail(''); setNewLabel(''); loadRecipients();
    } catch (err) {
      setAddErr(err.response?.data?.detail || 'Failed to add');
    }
  }
  async function toggleEnabled(r) {
    await api.patch(`/admin/notification-recipients/${r.id}`, { enabled: !r.enabled });
    loadRecipients();
  }
  async function delRecipient(r) {
    if (!window.confirm(`Remove ${r.email}?`)) return;
    await api.delete(`/admin/notification-recipients/${r.id}`);
    loadRecipients();
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="label text-heritage mb-2 inline-flex items-center gap-1.5"><Bell size={12}/> Inbox</div>
          <h1 className="font-display text-4xl">Notifications {unread > 0 && <span className="text-heritage">({unread})</span>}</h1>
        </div>
        {unread > 0 && (
          <button onClick={readAll} data-testid="notifications-read-all" className="bg-admin border border-charcoal/15 px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 hover:border-forest">
            <Check size={14}/> Mark all read
          </button>
        )}
      </div>

      <HelpCallout
        title="Where the team gets nudged when something needs eyes."
        description="Whenever a public submission lands (award nominations, AI town copy, scraped events) a notification appears here. Add admin emails below to mirror these as email alerts."
        appearsAt={[
          { label: 'Sidebar badges', where: 'pending counts appear next to Awards + AI Inspirations' },
          { label: 'This inbox', where: 'live feed of all admin alerts' },
        ]}
      />

      {/* Inbox */}
      <div className="mb-12">
        {items.length === 0 ? (
          <div className="bg-stone/40 border border-dashed border-charcoal/15 rounded-3xl p-10 text-center" data-testid="notifications-empty">
            <Bell size={28} className="mx-auto text-muted/40 mb-3"/>
            <p className="text-muted">Nothing in your inbox. Quiet day.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((n) => (
              <article key={n.id} data-testid={`notification-${n.kind}-${n.id.slice(0, 6)}`}
                className={`bg-white border rounded-2xl p-5 md:p-6 flex items-start gap-4 ${n.read ? 'border-charcoal/10 opacity-70' : 'border-heritage/30'}`}>
                <div className={`shrink-0 mt-1 w-2 h-2 rounded-full ${n.read ? 'bg-charcoal/20' : 'bg-heritage'}`}/>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg leading-tight mb-1">{n.title}</div>
                  {n.body && <p className="text-sm text-charcoal/70 mb-2 line-clamp-2">{n.body}</p>}
                  <div className="flex items-center gap-3 text-xs text-muted">
                    <span>{new Date(n.created_at).toLocaleString('en-GB')}</span>
                    <span className="bg-stone px-2 py-0.5 rounded-full">{n.kind}</span>
                    {n.email_sent && <span className="text-forest inline-flex items-center gap-1"><Mail size={11}/> Emailed</span>}
                    {!n.email_sent && n.email_attempts?.length > 0 && (
                      <span className="text-charcoal/40 inline-flex items-center gap-1" title={`Provider: ${provider}`}><AlertCircle size={11}/> Email not sent</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {n.link && <Link to={n.link} className="bg-admin border border-charcoal/15 hover:border-forest px-3 py-1.5 rounded-full text-xs font-semibold">Open</Link>}
                  {!n.read && (
                    <button onClick={() => markRead(n.id)} data-testid={`notification-read-${n.id.slice(0,6)}`} className="bg-forest text-base px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1">
                      <Check size={11}/> Read
                    </button>
                  )}
                  <button onClick={() => delN(n.id)} className="text-charcoal/40 hover:text-heritage p-1.5">
                    <Trash2 size={13}/>
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      {/* Recipients */}
      <div>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <h2 className="font-display text-2xl">Email recipients</h2>
          <div className="text-xs uppercase tracking-widest text-muted inline-flex items-center gap-2">
            <Mail size={12}/>
            {emailConfigured
              ? <span className="text-forest font-semibold">Email delivery active ({provider})</span>
              : <span className="text-heritage font-semibold">Email provider not configured</span>}
          </div>
        </div>

        {!emailConfigured && (
          <div className="bg-mustard/15 border border-mustard/40 rounded-2xl p-4 md:p-5 mb-4 text-sm" data-testid="notifications-provider-warning">
            <strong>📬 Email alerts aren&apos;t switched on yet.</strong> Right now, new alerts just sit in the inbox above. To get them by email, ask your developer for the Resend setup, or check the docs at <code>backend/.env</code>.
          </div>
        )}

        {/* Manual test trigger */}
        <div className="bg-white border border-charcoal/10 rounded-2xl p-5 md:p-6 mb-4 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="notifications-tools">
          <div className="flex items-start justify-between gap-4 flex-wrap" data-testid="notifications-test-strip">
            <div>
              <div className="font-display text-lg">Send a test alert</div>
              <p className="text-muted text-sm mt-1">Fires the full pipeline — creates a notification + attempts delivery to every enabled recipient.</p>
              {testResult && (
                <div className="mt-2 text-sm" data-testid="notifications-test-result">
                  {testResult.configured
                    ? <span className="text-forest">✅ Provider configured — {testResult.notification?.email_attempts?.filter((a) => a.ok).length || 0}/{testResult.notification?.email_attempts?.length || 0} delivered</span>
                    : <span className="text-heritage">⚠️ Provider not configured — notification queued only. Add RESEND_API_KEY to backend/.env.</span>}
                </div>
              )}
            </div>
            <button onClick={sendTest} disabled={testing} data-testid="notifications-send-test-btn"
              className="bg-charcoal text-base px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2 hover:bg-heritage disabled:opacity-50 self-start">
              {testing ? <RefreshCw size={14} className="animate-spin"/> : <Send size={14}/>}
              Send test alert
            </button>
          </div>

          <div className="flex items-start justify-between gap-4 flex-wrap md:border-l md:border-charcoal/10 md:pl-6" data-testid="link-health-strip">
            <div>
              <div className="font-display text-lg">Run link-health check</div>
              <p className="text-muted text-sm mt-1">Scans every event, attraction and package URL for broken links. Runs automatically nightly at 03:15.</p>
              {linkResult && (
                <div className="mt-2 text-sm" data-testid="link-health-result">
                  {linkResult._status === 'running' || linkResult._status === 'timeout' || linkResult._status === 'error'
                    ? <span className={linkResult._status === 'error' ? 'text-heritage' : 'text-muted'}>{linkResult.message}</span>
                    : (linkResult.broken_count > 0
                        ? <span className="text-heritage">⚠️ {linkResult.broken_count} broken of {linkResult.checked} checked — see inbox above.</span>
                        : <span className="text-forest">✅ All {linkResult.checked} links healthy.</span>)}
                </div>
              )}
            </div>
            <button onClick={runLinkCheck} disabled={checkingLinks} data-testid="link-health-run-btn"
              className="bg-charcoal text-base px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2 hover:bg-heritage disabled:opacity-50 self-start">
              {checkingLinks ? <RefreshCw size={14} className="animate-spin"/> : <Link2 size={14}/>}
              Check links now
            </button>
          </div>
        </div>

        <form onSubmit={addRecipient} className="bg-white border border-charcoal/10 rounded-2xl p-5 mb-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-end" data-testid="notifications-add-form">
          <div className="md:col-span-5">
            <div className="label mb-1.5">Email address</div>
            <input required type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="alerts@goleicestershire.com"
              data-testid="recipient-email-input"
              className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"/>
          </div>
          <div className="md:col-span-4">
            <div className="label mb-1.5">Label (optional)</div>
            <input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="Sarah · Editor"
              data-testid="recipient-label-input"
              className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"/>
          </div>
          <div className="md:col-span-3">
            <button type="submit" data-testid="recipient-add-btn" className="bg-forest text-base px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2 hover:bg-forest-deep w-full justify-center">
              <Plus size={14}/> Add recipient
            </button>
          </div>
          {addErr && <div className="md:col-span-12 text-heritage text-sm">{addErr}</div>}
        </form>

        {recipients.length === 0 ? (
          <div className="text-muted text-sm">No recipients yet. Add at least one to start receiving email alerts (once the provider is configured).</div>
        ) : (
          <div className="space-y-2">
            {recipients.map((r) => (
              <div key={r.id} data-testid={`recipient-${r.email}`} className="bg-white border border-charcoal/10 rounded-xl px-4 py-3 flex items-center justify-between gap-3">
                <div>
                  <div className="font-semibold">{r.email}</div>
                  {r.label && <div className="text-xs text-muted">{r.label}</div>}
                </div>
                <div className="flex items-center gap-2">
                  <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                    <input type="checkbox" checked={r.enabled} onChange={() => toggleEnabled(r)} data-testid={`recipient-enabled-${r.email}`}/>
                    {r.enabled ? 'Enabled' : 'Paused'}
                  </label>
                  <button onClick={() => delRecipient(r)} className="text-charcoal/40 hover:text-heritage p-1.5" data-testid={`recipient-delete-${r.email}`}>
                    <Trash2 size={13}/>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
